import { Truck } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * `apps/web-admin/src/routes/delivery-notes/*`: button labels and states
 * quoted verbatim. `requiredPermission: 'deliverynote.read'` matches the
 * `NavPermission` that gates "Remitos" in `layout/navigation.ts`.
 */
export const DELIVERY_NOTES_MODULE: GuideModule = {
  id: 'delivery-notes',
  title: 'Remitos',
  icon: Truck,
  requiredPermission: 'deliverynote.read',
  priority: 9,
  summary:
    'Un remito se genera automáticamente para cada pedido confirmado de una fecha, con las cantidades congeladas: ya no cambian aunque después se apruebe una solicitud de cambio sobre ese pedido. Desde acá seguís su recorrido — despacho, entrega y firma de recepción — y podés imprimir el lote completo de una fecha o descargar el PDF de un remito puntual. La firma llega escaneada desde el reparto o se carga manualmente; si la firma se hizo en un horario muy distinto al de su carga, el remito queda marcado para revisión manual.',
  steps: [
    {
      title: 'Generar remitos manualmente si falta alguno',
      description:
        'Los remitos se generan solos al cerrar el corte de cada fecha. Si detectás que falta alguno (por ejemplo, por un pedido confirmado después de un problema puntual), hacé clic en "Generar remitos", elegí la fecha de servicio y confirmá: el sistema genera los remitos que falten para esa fecha y salta los pedidos que ya tienen uno.',
      keyTerms: ['remito'],
    },
    {
      title: 'Consultar el listado de remitos',
      description:
        'Entrá a Remitos y filtrá por fecha, empresa, sede o estado. El filtro "Sin firmar" muestra únicamente los remitos que ya se entregaron pero todavía no tienen la firma de recepción cargada.',
      keyTerms: ['remito', 'sede'],
    },
    {
      title: 'Despachar un remito',
      description:
        'Desde el detalle de un remito en estado "Pendiente", hacé clic en "Despachar" cuando sale del depósito hacia la sede.',
      keyTerms: ['remito'],
    },
    {
      title: 'Registrar la entrega',
      description:
        'Cuando el remito llegó a la sede, hacé clic en "Registrar entrega" para pasarlo a "Entregado" y habilitar la carga de la firma.',
    },
    {
      title: 'Cargar el remito firmado',
      description:
        'Hacé clic en "Cargar escaneo", elegí el archivo (PDF, JPEG o PNG, hasta 10 MB) y completá el nombre de quien recibe. Al confirmar la carga, el remito pasa a "Firmado".',
      keyTerms: ['remito-firmado'],
    },
    {
      title: 'Firmar en pantalla sin escanear nada',
      description:
        'Como alternativa a cargar un escaneo, en un remito "Entregado" podés hacer clic en "Firmar en pantalla": dibujá la firma en el recuadro, completá nombre y DNI de quien recibe, y hacé clic en "Confirmar firma". El remito pasa a "Firmado" con esa firma embebida en el PDF, sin pasar por ningún archivo.',
      keyTerms: ['firma-en-pantalla'],
    },
    {
      title: 'Registrar entrega y firma en un solo paso',
      description:
        'Si el remito todavía está "Despachado" y quien recibe puede firmar en el momento, hacé clic en "Registrar entrega y firma" en lugar de "Registrar entrega": la entrega y la firma en pantalla se registran juntas, y el remito pasa directo de "Despachado" a "Firmado".',
      keyTerms: ['firma-en-pantalla'],
    },
    {
      title: 'Instalar la app en el celular y usar "Mis entregas"',
      description:
        'Abrí el back-office desde el navegador del celular e instalalo: en Chrome, menú ⋮ → "Instalar aplicación"; en Safari, Compartir → "Agregar a pantalla de inicio". Con la app instalada, entrá a "Mis entregas": ves los remitos "Despachado" y "Entregado" de tus puntos de venta para hoy (o para mañana con el botón "Mañana"), con el número, la empresa, la sede y la dirección. Tocá un remito para ver los ítems y hacé clic en "Firmar entrega" para abrir el recuadro de firma. Arriba de la lista figura "Datos actualizados a las HH:MM", la hora de la última carga. Funciona sin señal si antes abriste esa fecha con conexión: si no hay conexión, ves los remitos guardados con "Sin conexión — mostrando lo último que cargaste a las HH:MM" y podés firmar igual. La firma queda guardada en el celular y el remito muestra "Firma guardada, falta enviarla"; mientras tanto no se puede firmar de nuevo ese remito. Se envía sola cuando vuelve la señal, al abrir la app y cada minuto; también podés tocar "Enviar ahora". Dejá la app abierta o volvé a abrirla con señal para que se sincronice, y no cierres sesión con firmas pendientes: si la sesión vence, iniciá sesión de nuevo con el mismo usuario y se envían. Si el sistema la rechaza, el remito muestra un aviso que podés quitar con "Descartar": "El remito fue anulado. Tu firma no se registró." significa que alguien anuló el remito mientras estabas sin señal; "Este remito ya fue firmado en otro dispositivo. Tu firma no se registró." significa que otra persona ya lo firmó; en ambos casos avisá a administración. Si aparece "Hay una versión nueva — Actualizar", tocá "Actualizar" cuando no estés firmando.',
      keyTerms: ['firma-en-pantalla'],
    },
    {
      title: 'Revisar una firma marcada para revisión manual',
      description:
        'Si la pestaña Firma muestra "Revisión manual requerida", la firma se hizo en un horario muy distinto al de su carga (por ejemplo, firmó sin conexión). Verificá los datos con el equipo de reparto antes de dar el remito por válido.',
      keyTerms: ['revision-de-remito'],
    },
    {
      title: 'Anular un remito',
      description:
        'Si un remito no corresponde (por ejemplo, se canceló el pedido antes de despacharlo), hacé clic en "Anular" e indicá el motivo, que queda visible arriba de todo en el detalle del remito. Un remito ya facturado no admite anulación.',
      keyTerms: ['anulacion-de-remito'],
    },
    {
      title: 'Imprimir los remitos del día',
      description:
        'Desde "Imprimir remitos del día", elegí la fecha y hacé clic en "Descargar PDF": se descarga un único archivo con los remitos de esa fecha que todavía no están firmados, listo para llevar al depósito.',
      keyTerms: ['remito'],
    },
    {
      title: 'Descargar el PDF de un remito puntual',
      description:
        'Desde el detalle de un remito, hacé clic en "Descargar PDF" para obtener ese remito solo.',
    },
  ],
  states: [
    {
      value: 'PENDING',
      label: 'Pendiente',
      description: 'Recién generado, todavía no salió del depósito.',
    },
    {
      value: 'DISPATCHED',
      label: 'Despachado',
      description: 'Salió del depósito hacia la sede.',
    },
    {
      value: 'DELIVERED',
      label: 'Entregado',
      description: 'Llegó a la sede. Todavía falta cargar la firma de recepción.',
    },
    {
      value: 'SIGNED',
      label: 'Firmado',
      description: 'Tiene la firma de recepción cargada.',
    },
    {
      value: 'INVOICED',
      label: 'Facturado',
      description: 'Ya se incluyó en una factura. No admite más cambios ni anulación.',
    },
    {
      value: 'VOID',
      label: 'Anulado',
      description: 'Se anuló con un motivo registrado. No cuenta para facturación.',
    },
  ],
  faq: [
    {
      question: '¿Por qué no puedo facturar un remito sin firmar?',
      answer:
        'Porque la firma de recepción es la prueba de que la entrega ocurrió. Un remito pasa a "Facturado" recién después de estar "Firmado" — sin esa firma, no hay entrega confirmada que facturar.',
    },
    {
      question: '¿Cómo corrijo un remito?',
      answer:
        'Un remito con datos incorrectos no se edita directamente: se anula, indicando el motivo, y se genera uno nuevo con los datos correctos.',
    },
    {
      question: '¿Qué significa "Revisión manual requerida" en la pestaña Firma?',
      answer:
        'Que la firma se hizo en un horario muy distinto al de su carga en el sistema (por ejemplo, se firmó sin conexión). No invalida la firma por sí sola, pero conviene verificarla antes de dar el remito por válido.',
    },
    {
      question: '¿Por qué "Imprimir remitos del día" descarga un solo PDF y no uno por remito?',
      answer:
        'Porque quien reparte necesita llevar un único archivo con todos los remitos de la fecha, en el mismo orden en que va a entregarlos, no una carpeta con un PDF suelto por cada uno.',
    },
  ],
  tips: [
    'Usá el filtro "Sin firmar" al final del día para ver qué entregas todavía necesitan la firma cargada.',
    'Revisá siempre la pestaña Firma antes de dar por cerrado un remito marcado con "Revisión manual requerida".',
    'Anulá y regenerá en lugar de intentar corregir un remito ya despachado: es el único camino previsto para un dato incorrecto.',
  ],
};
